import React, { useEffect, useState } from "react";
import classes from "./Vendors.module.scss";
import Dropdown, { DropdownOption } from "../../UI/Dropdown/Dropdown";
import VendorCard from "./VendorCard/VendorCard";

export interface ImageFormat {
  ext: string;
  url: string;
  hash: string;
  mime: string;
  name: string;
  path: string | null;
  size: number;
  width: number;
  height: number;
}

export interface Media {
  id: number;
  name: string;
  alternativeText: string;
  caption: string;
  width: number;
  height: number;
  formats: Partial<Record<string, ImageFormat>>;
  hash: string;
  ext: string;
  mime: string;
  size: number;
  url: string;
  previewUrl: string | null;
  provider: string;
  provider_metadata: unknown;
  created_at: string;
  updated_at: string;
}

export interface VendorCountry {
  id: number;
  locale: string;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  name: string;
  imageUrl: string | null;
  imageAltTag: string | null;
  vendor: number;
}

export interface VendorFeature {
  id: number;
  title: string;
  icon: string | null;
}

export interface VendorPromotionsAndFeatures {
  id: number;
  title: string;
  features: VendorFeature[];
}

export interface VendorButton {
  id: number;
  title: string;
  link: string | null;
  formId: string | null;
  toOnboardingEnabled: boolean | undefined;
  nearestOffice: boolean | undefined;
  lookingForCoaching: boolean | undefined;
}

export interface VendorCardDetails {
  id: number;
  title: string;
  promotionsAndFeatures: VendorPromotionsAndFeatures;
  learnMoreButton: VendorButton;
  enquireButton: VendorButton;
  additionalInfo: string;
  destinationHeading: string;
  disclaimerText: string;
}

export interface Vendor {
  id: number;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  title: string;
  logo: Media | null;
  countries: VendorCountry[];
  cardDetails: VendorCardDetails;
  disclaimer?: string;
  slug?: string;
}

export interface VendorsSectionData {
  id?: number;
  subHeading?: string;
  title?: string;
  description?: string;
}

interface VendorsProps {
  vendorsData?: Vendor[];
  vendorsSectionData?: VendorsSectionData;
  destinationOptions?: DropdownOption[];
  initialVisibleCount?: number;
  viewMoreStep?: number;
  onDestinationChange?: (option: DropdownOption | null) => void;
  onLearnMore?: (vendor: Vendor) => void;
  onEnquireNow?: (vendor: Vendor) => void;
}

const Vendors = ({
  vendorsData,
  vendorsSectionData,
  destinationOptions = [],
  initialVisibleCount = 6,
  viewMoreStep = 6,
  onDestinationChange,
  onLearnMore,
  onEnquireNow,
}: VendorsProps) => {
  const [selectedDestination, setSelectedDestination] = useState<DropdownOption | null>(null);
  const [visibleCount, setVisibleCount] = useState(initialVisibleCount);
  const [vendors, setVendors] = useState<Vendor[]>([]);

  useEffect(() => {
    setVendors(vendorsData ?? []);
    setSelectedDestination(null);
    setVisibleCount(initialVisibleCount);
  }, [vendorsData]);

  const handleDestinationChange = (option: DropdownOption) => {
    const filtered = (vendorsData ?? []).filter((vendor) => vendor?.countries?.some((country) => country?.name?.toLowerCase() === option.value.toLowerCase()));
    setVendors(filtered);
    setSelectedDestination(option);
    setVisibleCount(initialVisibleCount);
    onDestinationChange?.(option);
  };

  const handleDestinationClear = () => {
    setSelectedDestination(null);
    setVendors(vendorsData ?? []);
    setVisibleCount(initialVisibleCount);
    onDestinationChange?.(null);
  };

  const visibleVendors = vendors.slice(0, visibleCount);
  const hasMore = vendors.length > visibleCount;

  return (
    <section className={classes["vendors-section"]}>
      <div className={classes["header-container"]}>
        {vendorsSectionData?.subHeading && <p className={classes["eyebrow"]}>{vendorsSectionData.subHeading}</p>}
        {vendorsSectionData?.title && <h2 className={classes["title"]}>{vendorsSectionData.title}</h2>}
        {vendorsSectionData?.description && <div className={classes["description"]} dangerouslySetInnerHTML={{ __html: vendorsSectionData.description }} />}

        <div className={classes["dropdown-container"]}>
          <Dropdown
            options={destinationOptions}
            value={selectedDestination}
            onChange={handleDestinationChange}
            onClear={handleDestinationClear}
            showClear
            placeholder="Choose your Destination"
            className={classes["destination-dropdown"]}
            searchable
          />
        </div>
      </div>

      <div className={classes["vendors-grid"]}>
        {visibleVendors &&
          visibleVendors?.length > 0 &&
          visibleVendors.map((vendor) => <VendorCard key={vendor.id} vendor={vendor} onLearnMore={onLearnMore} onEnquireNow={onEnquireNow} />)}
      </div>

      {hasMore && (
        <div className={classes["view-more-container"]}>
          <button type="button" className={classes["view-more-btn"]} onClick={() => setVisibleCount((prev) => prev + viewMoreStep)}>
            View More
          </button>
        </div>
      )}
    </section>
  );
};

export default Vendors;
